import * as React from "react";

export type SideNavItem = {
  id: string;          // stable key + scroll anchor, e.g. "hero"
  label: string;       // visible label, e.g. "Heaven's Bridge"
};

export type SideNavTone = "primary" | "inverse";

type Props = {
  items: SideNavItem[];
  activeId: string;
  /** 0–1 fill of the active item's progress bar. Optional; defaults to 0. */
  activeProgress?: number;
  onSelect?: (id: string) => void;
  /** Color palette. "primary" = dark-blue on light scenes (default).
   *  "inverse" = white on dark scenes (services dusk-blue fog). */
  tone?: SideNavTone;
  className?: string;
  "aria-label"?: string;
};

export function SideNav({
  items,
  activeId,
  activeProgress = 0,
  onSelect,
  tone = "primary",
  className,
  "aria-label": ariaLabel = "Section anchors",
}: Props) {
  const fillPct = Math.max(0, Math.min(1, activeProgress)) * 100;

  return (
    <nav
      className={[
        "side-nav",
        tone === "inverse" ? "side-nav--inverse" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={ariaLabel}
    >
      <ul className="side-nav-list">
        {items.map((item) => {
          const isActive = item.id === activeId;
          const style = isActive
            ? ({ "--fill": `${fillPct}%` } as React.CSSProperties)
            : undefined;
          return (
            <li key={item.id} style={style}>
              <a
                className={["side-nav-item", isActive ? "is-active" : ""]
                  .filter(Boolean)
                  .join(" ")}
                href={`#${item.id}`}
                data-anchor={item.id}
                onClick={(e) => {
                  if (!onSelect) return;
                  e.preventDefault();
                  onSelect(item.id);
                }}
              >
                <span className="side-nav-marker" aria-hidden="true" />
                <span className="side-nav-label">{item.label}</span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
